/**
 * WorkspaceImpl - Implementation of the Workspace interface.
 *
 * This is the root container for the semantic domain model, managing:
 * - Domain hierarchy (root domains and their children)
 * - Bounded context registry (loaded contexts)
 *
 * Located in semantical-core with no dependencies on KnowledgeGraph,
 * Diagram, or other implementation-specific modules.
 */

import type { Workspace } from './Workspace.ts';
import type { Domain } from './Domain.ts';
import type { BoundedContext } from './BoundedContext.ts';

/**
 * Options for creating a WorkspaceImpl instance.
 */
export interface WorkspaceOptions {
    /** Unique identifier for the workspace */
    id?: string;
    /** Display name for the workspace */
    name?: string;
    /** Config version number */
    version?: number;
    /** Initial root domains */
    rootDomains?: Domain[];
}

/**
 * WorkspaceImpl - Concrete implementation of Workspace.
 *
 * Manages the domain hierarchy and bounded context registry.
 * UI components should interact through the Workspace interface.
 */
export class WorkspaceImpl implements Workspace {
    readonly id: string;
    readonly uri: string;
    readonly name: string;
    readonly version: number;

    private _rootDomains: Domain[] = [];
    private _boundedContexts: Map<string, BoundedContext> = new Map();

    constructor(options: WorkspaceOptions = {}) {
        this.id = options.id ?? `workspace-${Date.now()}`;
        this.uri = `urn:semantical:workspace:${this.id}`;
        this.name = options.name ?? 'Workspace';
        this.version = options.version ?? 1;

        // Initialize root domains if provided
        if (options.rootDomains) {
            this._rootDomains = [...options.rootDomains];
        }
    }

    // === Domain Hierarchy ===

    getRootDomains(): Domain[] {
        return [...this._rootDomains];
    }

    findDomain(id: string): Domain | undefined {
        const search = (domains: Domain[]): Domain | undefined => {
            for (const domain of domains) {
                if (domain.id === id) return domain;
                const found = search(domain.getChildren());
                if (found) return found;
            }
            return undefined;
        };
        return search(this._rootDomains);
    }

    getAllDomains(): Domain[] {
        const result: Domain[] = [];
        const collect = (domains: Domain[]) => {
            for (const domain of domains) {
                result.push(domain);
                collect(domain.getChildren());
            }
        };
        collect(this._rootDomains);
        return result;
    }

    addRootDomain(domain: Domain): void {
        if (!this._rootDomains.some(d => d.id === domain.id)) {
            this._rootDomains.push(domain);
        }
    }

    removeRootDomain(domainId: string): boolean {
        const index = this._rootDomains.findIndex(d => d.id === domainId);
        if (index >= 0) {
            this._rootDomains.splice(index, 1);
            return true;
        }
        return false;
    }

    // === Bounded Context Registry ===

    getBoundedContexts(): BoundedContext[] {
        return Array.from(this._boundedContexts.values());
    }

    findBoundedContext(id: string): BoundedContext | undefined {
        // First check registry
        if (this._boundedContexts.has(id)) {
            return this._boundedContexts.get(id);
        }
        // Fall back to hierarchy search
        for (const domain of this.getAllDomains()) {
            const bc = domain.getBoundedContexts().find(c => c.id === id);
            if (bc) return bc;
        }
        return undefined;
    }

    registerBoundedContext(context: BoundedContext): void {
        this._boundedContexts.set(context.id, context);
    }

    hasBoundedContext(id: string): boolean {
        return this._boundedContexts.has(id);
    }

    // === Serialization ===

    /**
     * Convert workspace to a plain JSON object.
     * Returns a structure compatible with WorkspaceConfigRoot.
     */
    toJSON(): {
        '@context': object;
        '@type': string;
        version: number;
        domains: unknown[];
    } {
        return {
            '@context': {
                '@vocab': 'http://semantical.io/ontology/core#',
                domains: { '@id': 'hasDomain', '@type': '@id', '@container': '@set' }
            },
            '@type': 'WorkspaceConfig',
            version: this.version,
            domains: this._rootDomains.map(d =>
                'toJSON' in d && typeof d.toJSON === 'function'
                    ? (d as { toJSON: () => unknown }).toJSON()
                    : d
            )
        };
    }
}
